import React, { createContext, useState, useContext, useEffect } from 'react';

const CourseProgressContext = createContext();
const STORAGE_KEY = 'courseProgress';

export const useProgress = () => useContext(CourseProgressContext);

export const CourseProgressProvider = ({ children }) => {
  const [courseProgress, setCourseProgress] = useState({});
  const [loading, setLoading] = useState(true);

  // Charger la progression depuis localStorage
  useEffect(() => {
    const savedProgress = localStorage.getItem(STORAGE_KEY);
    if (savedProgress) {
      setCourseProgress(JSON.parse(savedProgress));
    }
    setLoading(false);
  }, []);

  // Mettre à jour la progression pour une étape
  const updateStepProgress = async (courseId, chapterId, stepId, completed) => {
    try {
      const updatedProgress = {
        ...courseProgress,
        [courseId]: {
          ...(courseProgress[courseId] || {}),
          [chapterId]: {
            ...(courseProgress[courseId]?.[chapterId] || {}),
            [stepId]: completed
          }
        }
      };

      // Mettre à jour l'état local
      setCourseProgress(updatedProgress);
      
      // Sauvegarder dans localStorage
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedProgress));
      return true;
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la progression:', error);
      return false;
    }
  };

  // Vérifier si une étape est complétée
  const isStepCompleted = (chapterId, stepId) => {
    // On utilise le premier courseId trouvé car on ne gère qu'un seul cours
    const courseId = Object.keys(courseProgress)[0];
    return !!courseProgress?.[courseId]?.[chapterId]?.[`step${stepId}`];
  };

  // Vérifier si un chapitre est complété (toutes les étapes complétées)
  const isChapterCompleted = (chapterId) => {
    // On utilise le premier courseId trouvé car on ne gère qu'un seul cours
    const courseId = Object.keys(courseProgress)[0];
    const chapterProgress = courseProgress?.[courseId]?.[chapterId] || {};
    const completedSteps = Object.values(chapterProgress).filter(Boolean).length;
    return completedSteps >= 3; // Chaque chapitre a 3 étapes
  };

  // Calculer le pourcentage de progression pour un cours
  const getCourseProgress = (chapterStepsMap) => {
    // On utilise le premier courseId trouvé car on ne gère qu'un seul cours
    const courseId = Object.keys(courseProgress)[0] ;
    if (!courseProgress?.[courseId]) return 0;

    let completedSteps = 0;
    let totalSteps = 0;

    for (const [chapterId, stepsCount] of Object.entries(chapterStepsMap)) {
      const chapterProgress = courseProgress[courseId]?.[chapterId] || {};
      completedSteps += Object.values(chapterProgress).filter(Boolean).length;
      totalSteps += stepsCount;
    }

    return totalSteps > 0 ? Math.round((completedSteps / totalSteps) * 100) : 0;
  };

  return (
    <CourseProgressContext.Provider
      value={{
        courseProgress,
        loading,
        updateStepProgress,
        isStepCompleted,
        isChapterCompleted,
        getCourseProgress
      }}
    >
      {children}
    </CourseProgressContext.Provider>
  );
}; 